// MUI components and hooks for layout, theming, and UI
import { Box, Container, Typography, useMediaQuery, useTheme, Divider, CssBaseline } from '@mui/material';
import React, { useState, useEffect } from 'react';
import Loader from '../../commons/Loader'; // Loader for async actions
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
// Modular sign-up field and button components
import {
  addEmailAddress,
  addPassword,
  confirmPassword,
  privacyPolicy,
  termsOfUse,
  mailmeAboutJob,
  createAccountBtn,
  dividerWithOr,
  LinkedInBtn,
  alreadyHavingAccount
} from '../../commons/SignUpComponents';
import { useNavigate } from 'react-router'; // React Router navigation
// Custom styles and stepper for the sign-up page
import { ContainerStyle, contentWrapStyle, backImage, styledCardWrappers, titleStyle, formWrapper, Stepper, dividerStyle } from './NewSignupStyles';
import Api from '../../Api'; // API utility for backend calls
import { validateFields, validationOptions } from '../../ValidationSchema'; // Validation helpers
import { reactGAEvents, backgroundTopRight, backgroundTopRightDark } from '../../commons/commonFunctions'; // Analytics and background helpers

/**
 * NewSignUp
 * Sign-up page for new users (email/password only).
 * Handles user registration, validation, and error display.
 * Uses modular field components and adapts layout for mobile/tablet/desktop.
 */
const NewSignUp = () => {
  // Access the current MUI theme
  const theme = useTheme();
  // Get the current theme mode (light/dark)
  const themeStyle = theme.palette.mode;
  // Loading state for async registration
  const [loading, setLoading] = useState<boolean>(false);
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  // React Router navigation
  const navigate = useNavigate();

  // Form field states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfrim, setPasswordConfirm] = useState('');
  const [privacyPolicyChecked, setPrivacyPolicyCheck] = useState(false);
  const [termsOfUseChecked, setTermsOfUseChecked] = useState(false);
  // Checkbox for job opportunity reminders
  const [textOrCall, setTextOrCall] = useState(false);
  // Error messages for each field
  const [errors, setErrors] = useState<any>({});
  // Theme and device helpers
  const isLightMode = theme.palette.mode === 'light' ? 'light' :'dark';
  const isMobileOrTablet = isMobile || isTablet;

  /**
   * Handles form submission: validates fields, sends registration request, and handles errors.
   */
  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    console.log('GA CREATE ACCOUNT');
    reactGAEvents('profile-register', 'create-account');

    // Validate all fields
    const isValidField = validateFields.validate(
      {
        email: email,
        password: password,
        confirmPassword: passwordConfrim,
        privacyPolicy: privacyPolicyChecked,
        termsOfUse: termsOfUseChecked,
      },
      validationOptions
    );

    // If any validation errors, collect and display them
    if (isValidField?.error) {
      let errorKeys: any = { email: '', password: '', privacy_policy: '', terms_of_use: '' };
      const contextKeyMapping: any = {
        email: 'email',
        password: 'password', //NOSONAR
        confirmPassword: 'password', //NOSONAR
        privacyPolicy: 'privacy_policy',
        termsOfUse: 'terms_of_use',
      };
      errorKeys = isValidField.error.details?.reduce((acc:any, val:any) => {
        const mappedKey:any = contextKeyMapping[val?.context?.key];
        if (mappedKey) {
          acc[mappedKey] = val?.message;
        }
        return acc;
      }, { email: '', password: '', privacy_policy: '', terms_of_use: '' });
      setErrors(errorKeys);
      return;
    }

    // If validation passes, clear errors and build payload
    setErrors({});
    const payload: any = {
      email: email,
      password: password,
      job_opportunities_reminders_opt_in: textOrCall,
      privacy_policy: privacyPolicyChecked,
      terms_of_use: termsOfUseChecked,
    };

    // Call API to register user
    setLoading(true);
    Api.register(payload)
      .then((response) => {
        if (response.success) {
          // On success, navigate to account activation page
          navigate('/account-activation');
        } else {
          // On error, display error messages
          const { data }:any = response;
          setErrors(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        // Log error and stop loading
        console.log(err);
        setLoading(false);
      });
  };

  // On mount, redirect to /home if already authorized (user is logged in)
  useEffect(() => {
    let isAuth = Api.isAuthorized();
    if (isAuth) navigate('/home');
  }, []);

  // Background images for light/dark and device
  const backgroundLight = isMobileOrTablet ? 'url(../res/NewPasswordBGLightMobile.svg)' : 'url(../res/NewPasswordBGLight.svg)';
  const backgroundDark = isMobileOrTablet ? 'url(../res/NewPasswordBGDarkMobile.svg)' : 'url(../res/NewPasswordBGDark.svg)';

  return (
    <>
      {/* Normalize browser styles for consistency */}
      <CssBaseline />
      {/* Show loader while registering */}
      {loading && <Loader />}
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based color/gradient */}
      <Box sx={themeStyle === 'light' ? { ...backgroundTopRight, display: 'grid' } : { ...backgroundTopRightDark, display: 'grid' }}>
        <Container maxWidth="lg" sx={ContainerStyle(isMobile, isTablet)}>
          {/* Decorative background image for the form card */}
          <Box
            sx={{
              background: isLightMode ? backgroundLight : backgroundDark,
              ...backImage(isMobile, isTablet),
            }}
          ></Box>
          {/* Main content wrapper for the form and stepper */}
          <Box sx={contentWrapStyle(isMobile, isTablet)}>
            {/* Stepper indicator (step 0) */}
            {Stepper(0)}
            <Box sx={styledCardWrappers(isMobile, isTablet)}>
              {/* Title for the sign-up form */}
              <Typography sx={titleStyle(themeStyle)}>Start by creating your Heidrick for Leaders account.</Typography>
              <Box sx={formWrapper}>
                {/* Modular form fields and controls */}
                {addEmailAddress(reactGAEvents, email, setEmail, errors)}
                {addPassword(reactGAEvents, password, setPassword, errors)}
                {confirmPassword(reactGAEvents, passwordConfrim, setPasswordConfirm, errors)}
                {privacyPolicy(themeStyle, reactGAEvents, privacyPolicyChecked, setPrivacyPolicyCheck, errors)}
                {termsOfUse(themeStyle, reactGAEvents, termsOfUseChecked, setTermsOfUseChecked, errors)}
                {mailmeAboutJob(reactGAEvents, textOrCall, setTextOrCall)}
                {createAccountBtn(themeStyle, reactGAEvents, handleSubmit)}
                {dividerWithOr(themeStyle)}
                {/* LinkedIn sign-up and divider */}
                {LinkedInBtn(themeStyle, reactGAEvents, setErrors)}
                <Divider sx={dividerStyle(themeStyle)} />
                {/* Link to sign in for existing users */}
                {alreadyHavingAccount(themeStyle, reactGAEvents, navigate, isMobile)}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
};

export default NewSignUp;
